import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BookkeepingDocumentJson,
  CreateBookkeepingDocumentResponse,
  GetBookkeepingDocumentResponse,
  CreateApprovalNumberResponse,
} from "../../models/BookkeepingDocument.model";
import {
  createBookkeepingDocument,
  getBookkeepingDocument,
  createApprovalNumberForExistingDocument,
} from "../thunks/bookkeepingDocumentThunks";

interface BookkeepingDocumentState {
  loading: boolean;
  error: string | null;
  createLoading: boolean;
  createError: string | null;
  lastCreatedDocumentId: number | null;
  bookkeepingDocumentJson: BookkeepingDocumentJson | null;
  approvalNumberLoading: boolean;
  approvalNumberError: string | null;
  approvalNumberResult: CreateApprovalNumberResponse | null;
}

const initialState: BookkeepingDocumentState = {
  loading: false,
  error: null,
  createLoading: false,
  createError: null,
  lastCreatedDocumentId: null,
  bookkeepingDocumentJson: null,
  approvalNumberLoading: false,
  approvalNumberError: null,
  approvalNumberResult: null,
};
const bookkeepingDocumentSlice = createSlice({
  name: "bookkeepingDocument",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
      state.createError = null;
    },
    clearLastCreatedDocumentId: (state) => {
      state.lastCreatedDocumentId = null;
    },
    resetCreateState: (state) => {
      state.createLoading = false;
      state.createError = null;
      state.lastCreatedDocumentId = null;
    },
    clearApprovalNumberError: (state) => {
      state.approvalNumberError = null;
    },
    resetApprovalNumberState: (state) => {
      state.approvalNumberLoading = false;
      state.approvalNumberError = null;
      state.approvalNumberResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // יצירת מסמך הנהלת חשבונות
      .addCase(createBookkeepingDocument.pending, (state) => {
        state.createLoading = true;
        state.createError = null;
        state.lastCreatedDocumentId = null;
      })
      .addCase(
        createBookkeepingDocument.fulfilled,
        (state, action: PayloadAction<CreateBookkeepingDocumentResponse>) => {
          state.createLoading = false;
          state.createError = null;
          state.lastCreatedDocumentId = action.payload.bookkeepingDocumentID;
        },
      )
      .addCase(createBookkeepingDocument.rejected, (state, action) => {
        state.createLoading = false;
        state.createError = action.payload as string;
        state.lastCreatedDocumentId = null;
      })
      .addCase(getBookkeepingDocument.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.bookkeepingDocumentJson = null;
      })
      .addCase(
        getBookkeepingDocument.fulfilled,
        (state, action: PayloadAction<GetBookkeepingDocumentResponse>) => {
          state.loading = false;
          state.error = null;
          state.bookkeepingDocumentJson = action.payload.bookkeepingDocument;
        },
      )
      .addCase(getBookkeepingDocument.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.bookkeepingDocumentJson = null;
      })
      // עדכון מספר הקצאה למסמך קיים
      .addCase(createApprovalNumberForExistingDocument.pending, (state) => {
        state.approvalNumberLoading = true;
        state.approvalNumberError = null;
        state.approvalNumberResult = null;
      })
      .addCase(
        createApprovalNumberForExistingDocument.fulfilled,
        (state, action: PayloadAction<CreateApprovalNumberResponse>) => {
          state.approvalNumberLoading = false;
          state.approvalNumberError = null;
          state.approvalNumberResult = action.payload;
        },
      )
      .addCase(createApprovalNumberForExistingDocument.rejected, (state, action) => {
        state.approvalNumberLoading = false;
        state.approvalNumberError =
          typeof action.payload === "string"
            ? action.payload
            : (action.payload as CreateApprovalNumberResponse)?.message || "שגיאה בעדכון מספר הקצאה";
        state.approvalNumberResult = null;
      });
  },
});
export const {
  clearError,
  clearLastCreatedDocumentId,
  resetCreateState,
  clearApprovalNumberError,
  resetApprovalNumberState,
} = bookkeepingDocumentSlice.actions;
export default bookkeepingDocumentSlice.reducer;
